/**
 * PDP main gallery arrows + desktop thumbnail layouts.
 *
 * Dawn often hides inactive slides (e.g. thumbnail desktop), so only one slide has layout width.
 * We navigate via media-gallery.setActiveMedia() instead (capture phase runs before SliderComponent).
 */
(function () {
  const MQ_DESKTOP = window.matchMedia('(min-width: 750px)');
  const slideSyncBound = new WeakSet();

  function getSlides(viewer) {
    return Array.from(viewer.querySelectorAll('li.product__media-item.slider__slide[data-media-id]'));
  }

  function visibleSlideCount(slides) {
    return slides.filter((el) => el.offsetParent !== null && el.clientWidth > 0).length;
  }

  /** True when only one slide is “visible” — SliderComponent scroll math breaks; use setActiveMedia instead */
  function shouldUseActiveMediaNav(viewer) {
    if (!MQ_DESKTOP.matches) return false;
    const slides = getSlides(viewer);
    if (slides.length < 2) return false;
    return visibleSlideCount(slides) <= 1;
  }

  function getActiveIndex(slides) {
    const idx = slides.findIndex((el) => el.classList.contains('is-active'));
    return idx >= 0 ? idx : 0;
  }

  function syncNavDisabled(viewer, activeIdx, slideCount) {
    const prevBtn = viewer.querySelector('button[name="previous"].product-media-gallery__nav-btn');
    const nextBtn = viewer.querySelector('button[name="next"].product-media-gallery__nav-btn');
    if (!prevBtn || !nextBtn) return;

    if (activeIdx <= 0) prevBtn.setAttribute('disabled', 'disabled');
    else prevBtn.removeAttribute('disabled');

    if (activeIdx >= slideCount - 1) nextBtn.setAttribute('disabled', 'disabled');
    else nextBtn.removeAttribute('disabled');
  }

  function navigateViewer(viewer, delta) {
    const slides = getSlides(viewer);
    if (slides.length < 2) return;

    const mediaGalleryEl = viewer.closest('media-gallery');
    let activeIdx = getActiveIndex(slides);
    let nextIdx = Math.min(slides.length - 1, Math.max(0, activeIdx + delta));
    if (nextIdx === activeIdx) return;

    const mediaId = slides[nextIdx].dataset.mediaId;

    if (mediaGalleryEl && typeof mediaGalleryEl.setActiveMedia === 'function') {
      mediaGalleryEl.setActiveMedia(mediaId, false);
    } else {
      slides.forEach((s) => s.classList.remove('is-active'));
      slides[nextIdx].classList.add('is-active');
    }

    syncNavDisabled(viewer, nextIdx, slides.length);
  }

  function bindSlideSync(viewer) {
    if (slideSyncBound.has(viewer)) return;
    slideSyncBound.add(viewer);

    const sliderUl = viewer.querySelector('[id^="Slider-Gallery"]');
    if (!sliderUl) return;

    const runSync = () => {
      if (!shouldUseActiveMediaNav(viewer)) return;
      const slides = getSlides(viewer);
      if (!slides.length) return;
      syncNavDisabled(viewer, getActiveIndex(slides), slides.length);
    };

    const mo = new MutationObserver(() => requestAnimationFrame(runSync));
    mo.observe(sliderUl, { subtree: true, attributes: true, attributeFilter: ['class'] });

    const mg = viewer.closest('media-gallery');
    if (mg) {
      mg.addEventListener(
        'click',
        (e) => {
          if (!e.target.closest('.thumbnail-slider')) return;
          requestAnimationFrame(runSync);
        },
        false
      );
    }

    const onMqChange = () => requestAnimationFrame(runSync);
    if (typeof MQ_DESKTOP.addEventListener === 'function') {
      MQ_DESKTOP.addEventListener('change', onMqChange);
    } else if (typeof MQ_DESKTOP.addListener === 'function') {
      MQ_DESKTOP.addListener(onMqChange);
    }
    requestAnimationFrame(runSync);
  }

  function initViewer(viewer) {
    const productRoot = viewer.closest('.product');
    if (!productRoot) return;

    if (shouldUseActiveMediaNav(viewer)) {
      const slides = getSlides(viewer);
      syncNavDisabled(viewer, getActiveIndex(slides), slides.length);
      bindSlideSync(viewer);
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const btn = event.target.closest('button.product-media-gallery__nav-btn');
      if (!btn) return;

      const viewer = btn.closest('slider-component.product-media-gallery__viewer');
      if (!viewer) return;

      if (!shouldUseActiveMediaNav(viewer)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const delta = btn.name === 'next' ? 1 : -1;
      navigateViewer(viewer, delta);
    },
    true
  );

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('slider-component.product-media-gallery__viewer').forEach(initViewer);
  });

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('slider-component.product-media-gallery__viewer').forEach(initViewer);
  });
})();
